import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Sparkles,
  Compass,
  Heart,
  Anchor,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import StickyCTA from "@/components/StickyCTA";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

// External booking link — Zanda client portal (replaces the previous
// internal waitlist scroll-target). Kept as a single const so any future
// URL change is a one-line update.
const ZANDA_BOOKING_URL = "https://clientportal.zandahealth.com/clientportal/pipbennett";

const PACKAGES = [
  {
    id: "single-session",
    variant: "single",  // renders as full-width entry-point row above the two-col grid
    label: "Single Strategic Coaching Session",
    descriptor: null,
    duration: null,
    body:
      "A focused 60-minute strategic coaching session for support with a specific decision, transition, challenge or point of clarity.",
    extra: null,
    creditNote:
      "If you choose to continue into Alignment Arc or Becoming within 14 days, your $330 session fee will be credited in full toward your chosen program. I\u2019ll arrange your package with you directly after your session.",
    investment: "$330 AUD",
    priceAud: 330,
    paymentPlans: null,
    followUp: null,
  },
  {
    id: "alignment-arc",
    label: "Alignment Arc",
    descriptor: "3-Month Strategic Coaching Program for Midlife Transition",
    duration: "3 months · 8 private sessions",
    body:
      "The foundation. Four intensive weekly sessions to begin, followed by fortnightly sessions as you integrate and build momentum.",
    extra:
      "Includes email access between sessions and your personalised workbook, built specifically for the patterns and goals we identify together. Clinical hypnotherapy is available within your sessions if and when it\u2019s the right fit \u2014 we\u2019ll decide together as the work unfolds.",
    investment: "$3,500 AUD",
    priceAud: 3500,
    paymentPlans: [
      { label: "Pay in full", amount: "$3,500" },
      { label: "Monthly payment plan", amount: "3 × $1,166.67" },
    ],
    followUp:
      "Begin with a Single Strategic Coaching Session. If we decide to continue together, I\u2019ll arrange your Alignment Arc package with you directly.",
  },
  {
    id: "the-becoming",
    label: "Becoming",
    descriptor: "6-Month Strategic Coaching Program for Midlife Reinvention",
    duration: "6 months · 14 private sessions",
    body:
      "For the person ready to go all the way in. The same intensive beginning, followed by six months of deep integration, identity-level shift and real-world application.",
    extra:
      "Includes email access between sessions and your personalised workbook \u2014 refined and built on as the work deepens across the full six months. Clinical hypnotherapy is available within your sessions if and when it\u2019s the right fit \u2014 we\u2019ll decide together as the work unfolds.",
    investment: "$6,200 AUD",
    priceAud: 6200,
    paymentPlans: [
      { label: "Pay in full", amount: "$6,200" },
      { label: "Monthly payment plan", amount: "6 × $1,033.33" },
    ],
    followUp:
      "Begin with a Single Strategic Coaching Session. If we decide to continue together, I\u2019ll arrange your Becoming package with you directly.",
  },
];

const PAYMENT_PLAN_OPTIONS = [
  { id: "full", label: "Pay in full", divisor: 1 },
  { id: "4x", label: "4 monthly payments", divisor: 4 },
  { id: "6x", label: "6 monthly payments", divisor: 6 },
];

function formatAud(n) {
  return n.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
}

const ARC_STEPS = [
  {
    month: "Month 1",
    title: "Safety and Seeing",
    headline: "We slow down before we speed up.",
    body:
      "Establishing internal safety, identifying the patterns running beneath the surface, beginning to distinguish between the noise and the truth. If it\u2019s the right fit for what\u2019s surfacing, the hypnotherapy work may begin here \u2014 working with the subconscious patterns that no amount of strategy alone has been able to shift.",
    icon: Anchor,
  },
  {
    month: "Month 2",
    title: "Clarity and Ownership",
    headline: "You begin to recognise yourself.",
    body:
      "Interrupting old loops, building self-trust, making decisions from internal authority rather than fear or expectation. The strategic life coaching layer deepens — we look at what you're building and why, and whether it's truly aligned.",
    icon: Compass,
  },
  {
    month: "Month 3",
    title: "Living It",
    headline: "This becomes who you are, not what you're trying to do.",
    body:
      "Embodying the shifts. Navigating the real world from the new baseline. Building the practices that sustain the change long after our work ends.",
    icon: Sparkles,
  },
  {
    month: "Months 4–6",
    title: "The Becoming",
    extension: true,
    headline: "The person who arrives here is not who you were when you started.",
    body:
      "Deeper identity work, navigating relationships and the external world from the new self, solidifying the life that actually feels like yours — in work, relationships, health and self-leadership.",
    icon: Heart,
  },
];

const FOR_YOU = [
  "The person who is high-functioning on the outside and quietly exhausted on the inside",
  "The person who has tried logic, planning and pushing harder — and it hasn't been enough",
  "The person who senses the answer isn't another strategy, it's something deeper",
  "The person who is ready to do the real work — not the performative kind",
];

const NOT_FOR_YOU = [
  "Someone looking for a quick fix or a five-step action plan",
  "Someone not yet ready to get genuinely honest with themselves",
  "Someone who wants to be told what to do",
];

function WaitlistForm({ initialInterest = "Not sure yet" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState(initialInterest);
  const [paymentPref, setPaymentPref] = useState("No preference yet");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    // Prepend payment preference to the message so Pip sees it when she
    // reviews the waitlist entry. The backend already accepts a free-form
    // message field, so no schema change required.
    const composedMessage = [
      `Payment preference: ${paymentPref}`,
      message ? `\n${message}` : "",
    ].join("").trim();
    try {
      await api.post("/waitlist", {
        name,
        email,
        interest,
        message: composedMessage || undefined,
      });
      setDone(true);
      toast.success("You're on the list. Pip will be in touch when a place opens.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't add you to the waitlist. Try again?"
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="bg-sage/5 border border-sage/30 rounded-2xl p-10 text-center"
        data-testid="ps-waitlist-success"
      >
        <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-4">
          <Check size={20} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif-display text-3xl text-ink mb-3">You're on the list.</h3>
        <p className="text-ink-soft leading-relaxed max-w-md mx-auto">
          When a place opens, Pip will be in touch personally. In the meantime, the program library
          is here whenever you're ready to begin.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white border border-hairline rounded-2xl p-8 md:p-10 space-y-5"
      data-testid="ps-waitlist-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="eyebrow block mb-2 text-ink-soft">First name</label>
          <input
            required
            type="text"
            autoComplete="given-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-cream-soft border border-hairline rounded-full px-5 py-3 text-ink focus:outline-none focus:border-sage transition"
            data-testid="ps-waitlist-name"
          />
        </div>
        <div>
          <label className="eyebrow block mb-2 text-ink-soft">Email address</label>
          <input
            required
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-cream-soft border border-hairline rounded-full px-5 py-3 text-ink focus:outline-none focus:border-sage transition"
            data-testid="ps-waitlist-email"
          />
        </div>
      </div>
      <div>
        <label className="eyebrow block mb-2 text-ink-soft">Which package interests you?</label>
        <select
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
          className="w-full bg-cream-soft border border-hairline rounded-full px-5 py-3 text-ink focus:outline-none focus:border-sage transition appearance-none"
          data-testid="ps-waitlist-interest"
        >
          <option>The Alignment Arc — 3 months</option>
          <option>The Becoming — 6 months</option>
          <option>Not sure yet</option>
        </select>
      </div>
      <div>
        <label className="eyebrow block mb-2 text-ink-soft">
          Preferred payment plan <span className="text-ink-soft/60 normal-case">(if it helps to know)</span>
        </label>
        <select
          value={paymentPref}
          onChange={(e) => setPaymentPref(e.target.value)}
          className="w-full bg-cream-soft border border-hairline rounded-full px-5 py-3 text-ink focus:outline-none focus:border-sage transition appearance-none"
          data-testid="ps-waitlist-payment-pref"
        >
          <option>No preference yet</option>
          <option>Pay in full</option>
          <option>4 monthly payments</option>
          <option>6 monthly payments</option>
        </select>
      </div>
      <div>
        <label className="eyebrow block mb-2 text-ink-soft">A note <span className="text-ink-soft/60 normal-case">(optional)</span></label>
        <textarea
          rows={4}
          placeholder="What's bringing you here? What would you like to shift?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full bg-cream-soft border border-hairline rounded-2xl px-5 py-4 text-ink placeholder-ink-soft/50 focus:outline-none focus:border-sage transition resize-none"
          data-testid="ps-waitlist-note"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all disabled:opacity-50"
        data-testid="ps-waitlist-submit"
      >
        {loading ? "Adding you…" : "Join the Waitlist"}
        {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
      </button>
    </form>
  );
}

export default function PrivateSessions() {
  useSeo({
    title: "Private Sessions · Pip Bennett — Strategic Life Coaching & Clinical Hypnotherapy",
    description:
      "1:1 private coaching with Pip Bennett. A blend of strategic life coaching and clinical hypnotherapy — for the person done with quick fixes and ready for something that lasts. Limited availability.",
    canonical: "https://pipbennett.com/private-sessions",
  });

  const scrollToWaitlist = () => {
    document
      .getElementById("private-waitlist")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="page-private-sessions">
      {/* HERO */}
      <section className="relative pt-12 md:pt-20 pb-20 md:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background: "linear-gradient(135deg, #F5EDE4 0%, #E3EBE4 60%, #F1E0D0 100%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <SectionReveal>
                <div className="eyebrow mb-6 text-gold-dark">Private Coaching</div>
                <h1
                  className="font-serif-display text-5xl md:text-6xl lg:text-[4.4rem] text-ink leading-[1.02]"
                  data-testid="ps-hero-title"
                >
                  This is the <em className="italic text-sage">deepest</em> way to work with me.
                </h1>
                <p className="mt-8 text-lg md:text-xl text-ink leading-relaxed">
                  Strategic life coaching, with clinical hypnotherapy available as part of the toolkit — working with both the conscious mind and the patterns running beneath it, when and if that&apos;s the right tool for you. For the person who is done with quick fixes and ready for something that actually lasts.
                </p>
              </SectionReveal>
            </div>
            <div className="lg:col-span-5">
              <SectionReveal delay={120}>
                <div className="relative">
                  <div className="absolute -inset-4 bg-sage/8 rounded-[2.5rem] -rotate-1 -z-10" />
                  <img
                    src="/private-sessions-hero-v1.png"
                    alt="Working with Pip Bennett online — private coaching delivered via video sessions"
                    className="w-full rounded-[1.75rem] shadow-[0_30px_80px_-20px_rgba(74,93,79,0.35)] object-cover aspect-[16/11]"
                    data-testid="ps-hero-image"
                    loading="eager"
                  />
                </div>
              </SectionReveal>
            </div>
          </div>
          <SectionReveal delay={220}>
            <div className="mt-16 md:mt-20 max-w-2xl space-y-5 text-ink-soft leading-relaxed">
              <p>
                I also offer a range of self-paced courses for those who prefer to begin the work
                independently or explore these approaches in their own time.
              </p>
              <p>
                This isn't a standard coaching package. There are no generic frameworks, no
                one-size-fits-all session plans. What we build together is specific to you — your
                patterns, your life, your particular brand of noise.
              </p>
              <p>
                Coaching is the foundation of every session. Clinical hypnotherapy sits alongside it as an optional tool — not a requirement. Not everyone wants or needs to work with the subconscious mind directly, and that&apos;s entirely fine. What we use, and when, is something we decide together, session by session, based on what&apos;s actually useful for you at that point in the work. For those who do want it, this combination is what creates shifts that hold.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT'S INCLUDED — added Aug 2026 per Pip: makes the "you're getting
          more than session time" story explicit before the price cards. */}
      <section className="py-20 md:py-28 bg-cream-soft border-y border-hairline">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-gold-dark">What&rsquo;s Included</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight max-w-3xl">
              What&rsquo;s included in every private package.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed max-w-2xl">
              Every client working with me privately receives more than session time. This is a fully
              supported container:
            </p>
          </SectionReveal>

          <div className="mt-12 md:mt-14 space-y-5">
            <SectionReveal>
              <div
                className="bg-white border border-hairline rounded-2xl p-7 md:p-9"
                data-testid="ps-included-sessions"
              >
                <div className="eyebrow text-sage mb-3">Private 1:1 sessions</div>
                <p className="text-ink leading-relaxed">
                  The number and rhythm depends on your package (see below).
                </p>
              </div>
            </SectionReveal>
            <SectionReveal delay={80}>
              <div
                className="bg-white border border-hairline rounded-2xl p-7 md:p-9"
                data-testid="ps-included-email"
              >
                <div className="eyebrow text-sage mb-3">Email access between sessions*</div>
                <p className="text-ink leading-relaxed">
                  A direct line to me when something comes up, a pattern surfaces, or you need to
                  think something through before we next meet. This isn&rsquo;t a generic support
                  inbox; it&rsquo;s ongoing access to me, so the work doesn&rsquo;t stop the moment
                  the session ends.
                </p>
                <p className="mt-4 text-ink-soft text-sm leading-relaxed italic">
                  *Email support is available Monday to Friday. I aim to respond within 24 hours
                  during business days. Messages received on weekends will be responded to on the
                  next business day.
                </p>
              </div>
            </SectionReveal>
            <SectionReveal delay={160}>
              <div
                className="bg-white border border-hairline rounded-2xl p-7 md:p-9"
                data-testid="ps-included-workbook"
              >
                <div className="eyebrow text-sage mb-3">A personalised workbook, built for you</div>
                <p className="text-ink leading-relaxed">
                  Not a generic PDF handed to every client. Your workbook is developed specifically
                  around your patterns, your goals, and the work we&rsquo;re doing together. It holds
                  the information and reflective tasks relevant to your process, so the insights from
                  each session have somewhere to land and keep working between appointments.
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* PACKAGE CARDS */}
      <section className="py-20 md:py-28 bg-cream-soft border-y border-hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-sage">The Packages</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight max-w-3xl">
              Three ways to begin. All built for depth.
            </h2>
          </SectionReveal>

          {/* Single Strategic Coaching Session — full-width entry-point row,
              rendered above the two commitment packages so it visually reads
              as "start here" without competing with the 3/6-month cards. */}
          {PACKAGES.filter((p) => p.variant === "single").map((pkg) => (
            <SectionReveal key={pkg.id}>
              <div
                className="mt-14 bg-white border border-hairline rounded-[1.5rem] p-8 md:p-10 lg:p-12 flex flex-col lg:flex-row lg:items-start lg:gap-10"
                data-testid={`ps-package-${pkg.id}`}
              >
                <div className="lg:flex-1">
                  <div className="eyebrow text-gold-dark mb-3">{pkg.label}</div>
                  <p className="text-ink leading-relaxed max-w-2xl">{pkg.body}</p>
                  {pkg.creditNote && (
                    <p className="mt-4 text-ink-soft leading-relaxed text-[0.97rem] italic max-w-2xl">
                      {pkg.creditNote}
                    </p>
                  )}
                </div>
                <div className="mt-8 lg:mt-0 lg:w-72 lg:flex-shrink-0 lg:border-l lg:border-hairline lg:pl-10 pt-8 lg:pt-0 border-t border-hairline lg:border-t-0">
                  <div className="eyebrow text-ink-soft mb-1">Investment</div>
                  <div className="font-serif-display text-3xl text-ink mb-6">
                    {pkg.investment}
                  </div>
                  <a
                    href={ZANDA_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                    data-testid={`ps-package-${pkg.id}-cta`}
                  >
                    Book your first session
                    <ArrowRight size={16} strokeWidth={1.8} />
                  </a>
                </div>
              </div>
            </SectionReveal>
          ))}

          {/* Two commitment packages — Alignment Arc + Becoming, side-by-side */}
          <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {PACKAGES.filter((p) => p.variant !== "single").map((pkg, i) => (
              <SectionReveal key={pkg.id} delay={i * 100}>
                <div
                  className="bg-white border border-hairline rounded-[1.5rem] p-10 md:p-12 h-full flex flex-col"
                  data-testid={`ps-package-${pkg.id}`}
                >
                  <div className="eyebrow text-sage mb-2">{pkg.label}</div>
                  {pkg.descriptor && (
                    <p className="font-serif-display italic text-lg text-ink-soft mb-5 leading-snug">
                      {pkg.descriptor}
                    </p>
                  )}
                  <div className="text-sm text-ink-soft mb-7">{pkg.duration}</div>
                  <p className="text-ink leading-relaxed">{pkg.body}</p>
                  {pkg.extra && (
                    <p className="mt-4 text-ink-soft leading-relaxed text-[0.97rem] flex-1">
                      {pkg.extra}
                    </p>
                  )}
                  <div className="mt-10 pt-6 border-t border-hairline">
                    <div className="eyebrow text-ink-soft mb-1">Investment</div>
                    <div className="font-serif-display text-3xl text-ink mb-5">
                      {pkg.investment}
                    </div>
                    {pkg.paymentPlans && pkg.paymentPlans.length > 0 && (
                      <div
                        className="mb-7 space-y-1.5 text-sm"
                        data-testid={`ps-package-${pkg.id}-plans`}
                      >
                        <div className="text-ink-soft/60 text-xs uppercase tracking-[0.16em] mb-2">
                          Payment options
                        </div>
                        {pkg.paymentPlans.map((opt) => (
                          <div
                            key={opt.label}
                            className="flex items-baseline justify-between text-ink-soft"
                          >
                            <span>{opt.label}</span>
                            <span className="tabular-nums text-ink">{opt.amount}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <a
                      href={ZANDA_BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                      data-testid={`ps-package-${pkg.id}-cta`}
                    >
                      Book your first session
                      <ArrowRight size={16} strokeWidth={1.8} />
                    </a>
                    {pkg.followUp && (
                      <p className="mt-4 text-ink-soft leading-relaxed text-sm italic text-center">
                        {pkg.followUp}
                      </p>
                    )}
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* GROWTH ARC */}
      <section className="py-20 md:py-28 bg-sage text-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-4 text-gold">The Arc</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight max-w-3xl">
              How the journey unfolds.
            </h2>
          </SectionReveal>
          <div className="mt-16 space-y-10 md:space-y-12">
            {ARC_STEPS.map((step, i) => (
              <SectionReveal key={i} delay={i * 80}>
                <div
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start"
                  data-testid={`ps-arc-step-${i + 1}`}
                >
                  <div className="md:col-span-3 flex md:flex-col items-center md:items-start gap-4">
                    <div className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-cream/10 border border-cream/20 text-gold flex-shrink-0">
                      <step.icon size={22} strokeWidth={1.4} />
                    </div>
                    <div>
                      <div className="eyebrow text-gold mb-1">{step.month}</div>
                      <div className="font-serif-display text-2xl leading-tight">
                        {step.title}
                        {step.extension && (
                          <span className="ml-2 inline-block align-middle text-[0.6rem] tracking-widest uppercase text-cream/55 bg-cream/10 px-2.5 py-1 rounded-full">
                            6-month extension
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-9 md:pt-3">
                    <p className="font-serif-display text-xl md:text-2xl italic text-gold leading-snug">
                      {step.headline}
                    </p>
                    <p className="mt-4 text-cream/80 leading-relaxed">{step.body}</p>
                  </div>
                </div>
                {i < ARC_STEPS.length - 1 && (
                  <div className="hidden md:block ml-7 mt-10 mb-0 w-px h-10 bg-cream/15" />
                )}
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 md:py-28 bg-cream-soft border-b border-hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <SectionReveal>
              <div className="bg-white border border-sage/30 rounded-2xl p-8 md:p-10 h-full">
                <div className="eyebrow mb-5 text-sage">Who this is for</div>
                <ul className="space-y-4 text-ink leading-relaxed">
                  {FOR_YOU.map((item, i) => (
                    <li key={i} className="flex gap-3" data-testid={`ps-for-you-${i + 1}`}>
                      <Check size={18} strokeWidth={2} className="text-sage flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </SectionReveal>
            <SectionReveal delay={120}>
              <div className="bg-white border border-hairline rounded-2xl p-8 md:p-10 h-full">
                <div className="eyebrow mb-5 text-ink-soft">Who this is not for</div>
                <ul className="space-y-4 text-ink-soft leading-relaxed">
                  {NOT_FOR_YOU.map((item, i) => (
                    <li key={i} className="flex gap-3" data-testid={`ps-not-for-you-${i + 1}`}>
                      <span className="text-ink-soft/50 flex-shrink-0 mt-0.5">—</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </SectionReveal>
          </div>
          <SectionReveal delay={200}>
            <p className="mt-14 md:mt-16 text-center font-serif-display text-2xl md:text-3xl text-ink leading-snug italic max-w-3xl mx-auto">
              If you're still reading, you already know which one you are.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* NEW-CLIENT INTAKE — for clients Pip has already spoken with. Kept
          available for anyone ready to formally begin (post-booking intake). */}
      <section className="py-20 md:py-28 bg-cream-soft border-t border-hairline">
        <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="eyebrow mb-4 text-gold-dark">Already spoken with Pip?</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              Ready to formally <em className="italic text-sage">begin</em>?
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              If we&apos;ve had a discovery conversation and you&apos;re ready to start, complete
              your new-client intake below. It takes 10–12 minutes and gives Pip everything she
              needs before your first session.
            </p>
            <Link
              to="/private-sessions/intake"
              className="mt-9 inline-flex items-center gap-2 bg-ink hover:bg-ink/90 text-cream rounded-full px-8 py-4 font-bold tracking-wide transition-all"
              data-testid="ps-start-intake-cta"
            >
              Start Your Client Intake
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <p className="mt-4 text-xs text-ink-soft/70">
              Private &amp; confidential · ICF-aligned informed consent
            </p>
          </SectionReveal>
        </div>
      </section>

      <StickyCTA
        label="Book your first session"
        href={ZANDA_BOOKING_URL}
        testId="sticky-cta-private-sessions"
      />
    </div>
  );
}
